import { EventType, PluginName, isThirdParty, pageOrigin, isCountableError, } from '@replayops/rrweb-schema';
/** Core Web Vitals thresholds (Google): good / needs-improvement / poor. */
function rateVital(metric, value) {
    const t = {
        LCP: [2500, 4000],
        FID: [100, 300],
        CLS: [0.1, 0.25],
    };
    const bounds = t[metric];
    if (!bounds)
        return 'good';
    if (value <= bounds[0])
        return 'good';
    if (value <= bounds[1])
        return 'needs-improvement';
    return 'poor';
}
/** The SDK's own ingest/config calls to ReplayOps — never shown in the Network
 * panel (older recordings captured them before the SDK learned to skip them). */
function isReplayOpsSelfCall(url) {
    return /\/v1\/(collect|config)(\?|#|$)/.test(url);
}
/** A network call is "slow" past this threshold (ms). */
const SLOW_MS = 1000;
/** Extension → type; checked before initiator so a CSS-initiated .woff2 is a font. */
const EXT_TYPE = [
    [/\.(js|mjs|cjs)([?#]|$)/i, 'js'],
    [/\.css([?#]|$)/i, 'css'],
    [/\.(png|jpe?g|gif|webp|svg|ico|avif|bmp)([?#]|$)/i, 'img'],
    [/\.(woff2?|ttf|otf|eot)([?#]|$)/i, 'font'],
    [/\.(mp4|webm|ogv|ogg|mp3|wav|m3u8)([?#]|$)/i, 'media'],
];
const INITIATOR_TYPE = {
    script: 'js',
    link: 'css',
    css: 'css',
    img: 'img',
    image: 'img',
    input: 'img',
    video: 'media',
    audio: 'media',
};
function classifyResource(kind, url, initiator) {
    if (kind === 'fetch' || kind === 'xhr')
        return 'xhr';
    for (const [re, t] of EXT_TYPE)
        if (re.test(url))
            return t;
    return (initiator && INITIATOR_TYPE[initiator]) || 'other';
}
/** Narrows an unknown payload field to a string→string map. */
function isStrMap(v) {
    return !!v && typeof v === 'object' && !Array.isArray(v);
}
export function extractInspectors(events) {
    const start = events.length ? Math.min(...events.map((e) => e.timestamp)) : 0;
    const data = { console: [], network: [], errors: [], vitals: [], perf: [] };
    const seenVital = new Map(); // keep the latest value per metric
    const origin = pageOrigin(events);
    for (const e of events) {
        if (e.type !== EventType.Plugin)
            continue;
        const offsetMs = e.timestamp - start;
        const { plugin, payload } = e.data;
        if (plugin === PluginName.Console) {
            data.console.push({
                offsetMs,
                level: String(payload.level ?? 'log'),
                message: Array.isArray(payload.args) ? payload.args.join(' ') : String(payload.args ?? ''),
            });
        }
        else if (plugin === PluginName.Network && !isReplayOpsSelfCall(String(payload.url ?? ''))) {
            const status = Number(payload.status ?? 0);
            const durationMs = Number(payload.durationMs ?? 0);
            const kind = String(payload.type ?? 'fetch');
            const isResource = kind === 'resource';
            data.network.push({
                offsetMs,
                startMs: Math.max(0, offsetMs - durationMs),
                kind,
                resourceType: classifyResource(kind, String(payload.url ?? ''), payload.initiator ? String(payload.initiator) : undefined),
                sizeBytes: typeof payload.sizeBytes === 'number' ? payload.sizeBytes : undefined,
                method: String(payload.method ?? 'GET').toUpperCase(),
                url: String(payload.url ?? ''),
                status,
                durationMs,
                error: payload.error ? String(payload.error) : undefined,
                traceId: payload.traceId ? String(payload.traceId) : undefined,
                reqBody: payload.reqBody != null ? String(payload.reqBody) : undefined,
                resBody: payload.resBody != null ? String(payload.resBody) : undefined,
                reqHeaders: isStrMap(payload.reqHeaders) ? payload.reqHeaders : undefined,
                resHeaders: isStrMap(payload.resHeaders) ? payload.resHeaders : undefined,
                operationName: payload.operationName ? String(payload.operationName) : undefined,
                operationType: payload.operationType ? String(payload.operationType) : undefined,
                // A third-party opaque beacon (status 0, e.g. analytics) isn't a
                // failure; nor is a static resource whose status the timing API hides.
                ok: isResource
                    ? status === 0 || status < 400
                    : (status >= 200 && status < 400) || (status === 0 && isThirdParty(String(payload.url ?? ''), origin)),
                slow: durationMs >= SLOW_MS,
            });
        }
        else if (plugin === PluginName.Performance && payload.sample) {
            data.perf.push({
                offsetMs,
                fps: typeof payload.fps === 'number' ? Math.round(payload.fps) : undefined,
                domNodes: typeof payload.domNodes === 'number' ? payload.domNodes : undefined,
                memoryMB: typeof payload.memoryMB === 'number' ? payload.memoryMB : undefined,
            });
        }
        else if (plugin === PluginName.Error && isCountableError(payload, origin)) {
            data.errors.push({
                offsetMs,
                kind: String(payload.kind ?? 'error'),
                message: String(payload.message ?? 'Error'),
                source: payload.source ? String(payload.source) : undefined,
                line: typeof payload.line === 'number' ? payload.line : undefined,
                stack: payload.stack ? String(payload.stack) : undefined,
            });
        }
        else if (plugin === PluginName.WebVitals) {
            const metric = String(payload.metric ?? '');
            const value = Number(payload.value ?? 0);
            seenVital.set(metric, value);
        }
    }
    // Emit one entry per metric (the latest reported value).
    for (const [metric, value] of seenVital) {
        data.vitals.push({ offsetMs: 0, metric, value, rating: rateVital(metric, value) });
    }
    // DevTools order: by request start (events arrive stamped at completion).
    data.network.sort((a, b) => a.startMs - b.startMs);
    return data;
}
//# sourceMappingURL=inspectors.js.map